<template>
    <div class="area-node" v-if="visible">
        <div class="node-row" :style="{ paddingLeft: (depth * 22 + 4) + 'px' }">

            <!-- Caret buka/tutup -->
            <span class="node-caret" v-if="node.level !== 'Kelurahan'" @click="onExpand(node)">
                <b-spinner v-if="node.loading" small class="caret-spinner"></b-spinner>
                <i v-else class="fa" :class="node.expanded ? 'fa-caret-down' : 'fa-caret-right'"></i>
            </span>
            <span class="node-caret placeholder" v-else></span>

            <!-- Checkbox tri-state -->
            <span
                class="node-check"
                :class="{ checked: checked, indeterminate: indeterminate, locked: lockedByParent, taken: dipakaiWilayahLain !== null }"
                :title="judulKunci"
                @click="onToggle(node)"
            >
                <i class="fa fa-check" v-if="checked"></i>
                <span class="dash" v-else-if="indeterminate">&ndash;</span>
            </span>

            <span class="node-name" :class="{ 'is-selected': ownSelected, 'is-taken': dipakaiWilayahLain !== null }" @click="onToggle(node)">{{ node.name }}</span>
            <span class="node-level">{{ node.level }}</span>
            <span class="node-taken" v-if="dipakaiWilayahLain !== null">{{ dipakaiWilayahLain }}</span>
        </div>

        <!-- Anak (rekursif) -->
        <div v-if="node.expanded && node.children.length > 0">
            <area-tree-node
                v-for="child in node.children"
                :key="child.key"
                :node="child"
                :selected="selected"
                :taken="taken"
                :keyword="keyword"
                :depth="depth + 1"
                :on-toggle="onToggle"
                :on-expand="onExpand"
            />
        </div>

        <div class="node-empty" v-else-if="node.expanded && node.loaded && node.children.length === 0"
            :style="{ paddingLeft: ((depth + 1) * 22 + 26) + 'px' }">
            Tidak ada data.
        </div>
    </div>
</template>

<script>
export default {
    name: 'area-tree-node',
    props: {
        node: { type: Object, required: true },
        selected: { type: Array, required: true },
        // Area milik wilayah lain: [{ key, path, wilayahNama }]
        taken: { type: Array, default: () => [] },
        keyword: { type: String, default: "" },
        depth: { type: Number, default: 0 },
        onToggle: { type: Function, required: true },
        onExpand: { type: Function, required: true },
    },
    computed: {

        // Dicentang bila node ini sendiri terpilih, atau tercakup oleh
        // salah satu leluhurnya yang terpilih.
        ownSelected() {
            return this.selected.some(s => s.key === this.node.key)
        },

        lockedByParent() {
            return this.selected.some(s => this.node.path.indexOf(s.key) !== -1)
        },

        checked() {
            return this.ownSelected || this.lockedByParent
        },

        /**
         * Nama wilayah lain yang membuat node ini tidak bisa dipilih, atau null.
         * Bentrok bila: node ini persis area milik wilayah lain, tercakup olehnya,
         * atau justru mencakupnya (memilih provinsi menabrak kelurahan di dalamnya).
         */
        dipakaiWilayahLain() {
            for (let t of this.taken) {
                if (t.key === this.node.key) return t.wilayahNama
                if (this.node.path.indexOf(t.key) !== -1) return t.wilayahNama
                if ((t.path || []).indexOf(this.node.key) !== -1) return t.wilayahNama
            }
            return null
        },

        judulKunci() {
            if (this.dipakaiWilayahLain !== null) {
                return `Sudah dipakai wilayah "${this.dipakaiWilayahLain}". Satu area hanya boleh masuk satu wilayah.`
            }
            if (this.lockedByParent) {
                return 'Sudah tercakup oleh area di atasnya. Hapus centang di level tersebut.'
            }
            return ''
        },

        // Sebagian anak/cucunya terpilih, tapi node ini sendiri tidak.
        indeterminate() {
            if (this.checked) return false
            return this.selected.some(s => s.path.indexOf(this.node.key) !== -1)
        },

        // Pencarian hanya menyaring node yang sudah dimuat.
        // Node tetap tampil bila namanya cocok, atau ada keturunannya yang cocok.
        visible() {
            let kw = (this.keyword || "").trim().toLowerCase()
            if (kw === "") return true
            if (this.node.name.toLowerCase().indexOf(kw) !== -1) return true
            return this.hasMatchingChild(this.node, kw)
        }

    },
    methods: {
        hasMatchingChild(node, kw) {
            for (let child of node.children) {
                if (child.name.toLowerCase().indexOf(kw) !== -1) return true
                if (this.hasMatchingChild(child, kw)) return true
            }
            return false
        }
    }
}
</script>

<style lang="scss" scoped>
$deep-pine: #023831;
$ink-body: #16302B;
$ink-heading: #072D28;
$ink-muted: #7E918B;
$input-border: #D8E2DC;

.node-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 7px;
    padding-bottom: 7px;
    padding-right: 8px;
    border-bottom: 1px solid #F2F5F3;
    transition: background-color 0.12s ease;

    &:hover { background-color: #F8FAF9; }
}

.node-caret {
    width: 16px;
    text-align: center;
    color: $ink-muted;
    cursor: pointer;
    font-size: 12px;
    flex-shrink: 0;

    &.placeholder { cursor: default; }

    .caret-spinner { width: 10px; height: 10px; }
}

.node-check {
    width: 18px;
    height: 18px;
    border: 1.5px solid $input-border;
    border-radius: 4px;
    background-color: #FFFFFF;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s ease;

    i { font-size: 9px; color: #FFFFFF; }
    .dash { color: #FFFFFF; font-weight: 700; line-height: 1; }

    &.checked,
    &.indeterminate {
        background-color: $deep-pine;
        border-color: $deep-pine;
    }

    &.locked { cursor: not-allowed; opacity: 0.75; }

    &.taken {
        cursor: not-allowed;
        background-color: #F3F4F6;
        border-color: #E0E4E1;
    }

    &:hover:not(.locked) { border-color: $deep-pine; }
}

.node-name {
    font-size: 13px;
    color: $ink-body;
    cursor: pointer;

    &.is-selected { font-weight: 700; color: $ink-heading; }
}

.node-name.is-taken { color: #A9B7B1; }

.node-taken {
    font-size: 10.5px;
    color: #9A6B2F;
    background-color: #FBF1E3;
    border-radius: 4px;
    padding: 2px 7px;
    white-space: nowrap;
}

.node-level {
    font-size: 10.5px;
    color: $ink-muted;
    background-color: #F1F4F2;
    border-radius: 4px;
    padding: 2px 7px;
    letter-spacing: 0.02em;
}

.node-empty {
    font-size: 12px;
    color: $ink-muted;
    padding-top: 6px;
    padding-bottom: 6px;
}
</style>
